import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Mail, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { authService } from '@/utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: authService.getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="loading">Загрузка...</div>;
  }

  const statCards = [
    {
      title: t('admin.totalProducts'),
      value: stats?.total_products || 0,
      icon: Package,
      color: 'text-blue-600'
    },
    {
      title: t('admin.totalContacts'),
      value: stats?.total_contacts || 0,
      icon: Mail,
      color: 'text-green-600'
    }
  ];

  return (
    <div data-testid="admin-dashboard-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="font-heading text-4xl mb-8 tracking-tight">{t('admin.dashboard')}</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-background p-6 rounded-sm border border-border"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-2">
                      {card.title}
                    </p>
                    <p className="font-heading text-4xl">{card.value}</p>
                  </div>
                  <Icon className={`w-12 h-12 ${card.color}`} />
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-background p-6 rounded-sm border border-border mb-8"
        >
          <h2 className="font-heading text-2xl mb-4 tracking-tight">Категории</h2>
          <div className="space-y-3">
            {stats?.categories?.map((cat) => (
              <div key={cat.category} className="flex justify-between items-center py-2 border-b border-border">
                <span className="font-body uppercase tracking-wide">
                  {cat.category === 'rings' && (i18n.language === 'ru' ? 'Помолвочные кольца' : 'Saderināšanās gredzeni')}
                  {cat.category === 'earrings' && (i18n.language === 'ru' ? 'Серьги-пусеты' : 'Auskari')}
                  {cat.category === 'bracelets' && (i18n.language === 'ru' ? 'Эксклюзивные браслеты' : 'Ekskluzīvas aproces')}
                </span>
                <span className="font-heading text-xl">{cat.count}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-background p-6 rounded-sm border border-border"
        >
          <h2 className="font-heading text-2xl mb-4 tracking-tight">{t('admin.recentContacts')}</h2>
          <div className="space-y-4">
            {stats?.recent_contacts?.slice(0, 5).map((contact) => (
              <div key={contact.id} className="py-3 border-b border-border">
                <div className="flex justify-between items-start mb-2">
                  <p className="font-body font-medium">{contact.name}</p>
                  <p className="font-body text-sm text-muted-foreground">
                    {new Date(contact.created_at).toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'lv-LV')}
                  </p>
                </div>
                <p className="font-body text-sm text-muted-foreground">{contact.phone}</p>
                <p className="font-body text-sm mt-1">{contact.message}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};
